import { useUser } from '@/contexts/UserContext';
import { Feature, Permission } from '@/shared/types/permissionsTypes';
import { OrgRole, OrgType } from '../types/userRoleTypes';
import { ORG_ROLE_PERMISSIONS } from '../types/permissionsTypes';
import { useCallback } from 'react';
import { useUserRoles } from './userRoles';

/**
 * Hook to check user permissions for features
 */
export const usePermissions = () => {
  const { currentUser, orgRole } = useUser();
  const { isTenantUser, isCustomerUser } = useUserRoles();
  /**
   * Check if user has a specific permission for a feature in an organization
   */
  const hasPermission = useCallback((accessingOrgType: OrgType, feature: Feature, permission: Permission = Permission.READ): boolean => {
    if (!orgRole) return false;

    // PLATFORM permissions are not allowed for tenants or customers
    if (accessingOrgType === OrgType.PLATFORM && (isTenantUser || isCustomerUser)) {
      return false;
    }

    // TENANT permissions are not allowed for customers
    if (accessingOrgType === OrgType.TENANT && isCustomerUser) {
      return false;
    }

    const permissions = ORG_ROLE_PERMISSIONS[orgRole]?.[feature] ?? [];
    return permissions.includes(permission) || permissions.includes(Permission.ALL);
  }, [orgRole]);

  /**
   * Check if user has global dashboard access
   */
  const hasGlobalDashboardAccess = useCallback((): boolean => {
    if (!orgRole) return false;
    if (orgRole === OrgRole.PLATFORM_ADMIN) return true;
    return currentUser?.canAccessGlobalDashboard ?? false;
  }, [orgRole, currentUser?.canAccessGlobalDashboard]);

  return { hasPermission, hasGlobalDashboardAccess };
};
